<template>
  <div class="app-layout">
    <!-- Sidebar Navigation -->
    <aside class="sidebar" v-if="showNav">
      <div class="sidebar-header">
        <div class="logo-box">
          <svg viewBox="0 0 24 24" width="24" height="24" fill="white"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
        </div>
        <div class="brand-info">
          <span class="brand-name">IT Support</span>
          <span class="brand-user">{{ (authStore.user as any)?.full_name || 'System User' }}</span>
        </div>
      </div>

      <nav class="sidebar-nav">
        <router-link to="/" class="nav-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="7" height="7"></rect><rect x="14" y="3" width="7" height="7"></rect><rect x="14" y="14" width="7" height="7"></rect><rect x="3" y="14" width="7" height="7"></rect></svg>
          Dashboard
        </router-link>
        
        <router-link v-if="isAdminOrAgent" to="/board" class="nav-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path><polyline points="14 2 14 8 20 8"></polyline><line x1="16" y1="13" x2="8" y2="13"></line><line x1="16" y1="17" x2="8" y2="17"></line><polyline points="10 9 9 9 8 9"></polyline></svg>
          Kanban Board
        </router-link>

        <router-link v-if="isAdmin" to="/admin/manage" class="nav-item">
          <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path><circle cx="12" cy="7" r="4"></circle></svg>
          Administration
          <span v-if="authStore.adminNotifications > 0" class="badge-dot"></span>
        </router-link>
      </nav>

      <div class="sidebar-footer">
        <button @click="handleLogout" class="logout-btn">
          <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" stroke-width="2"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
          Sign Out
        </button>
      </div>
    </aside>

    <!-- Main Content Area -->
    <main class="main-content" :class="{ 'full-width': !showNav }">
      <router-view />
    </main>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, onUnmounted } from 'vue'
import { useAuthStore } from './stores/auth'
import { useRouter, useRoute } from 'vue-router'

const authStore = useAuthStore()
const router = useRouter()
const route = useRoute()

let pollTimer: any = null

const showNav = computed(() => {
  return authStore.isAuthenticated && 
         !['login', 'request-access', 'forgot-password'].includes(route.name as string)
})

const isAdmin = computed(() => (authStore.user as any)?.role === 'ADMIN')
const isAdminOrAgent = computed(() => ['ADMIN', 'AGENT'].includes((authStore.user as any)?.role))

const handleLogout = () => {
  authStore.logout()
  router.push({ name: 'login' })
}

onMounted(() => {
  authStore.initialize()
  pollTimer = setInterval(() => authStore.fetchNotifications(), 30000)
  authStore.fetchNotifications()
})

onUnmounted(() => { if (pollTimer) clearInterval(pollTimer) })
</script>

<style>
:root {
  --sidebar-width: 260px;
  --primary: #000000;
  --accent: #2563eb;
  --bg: #f8fafc;
  --text-main: #1e293b;
  --text-muted: #64748b;
  --border: #e2e8f0;
}

* { box-sizing: border-box; }
body { 
  margin: 0; 
  font-family: 'Inter', -apple-system, sans-serif; 
  background-color: var(--bg);
  color: var(--text-main);
  -webkit-font-smoothing: antialiased;
}

.app-layout { display: flex; min-height: 100vh; }

/* SIDEBAR STYLES */
.sidebar {
  width: var(--sidebar-width);
  background: white;
  border-right: 1px solid var(--border);
  display: flex;
  flex-direction: column;
  position: fixed;
  height: 100vh;
  z-index: 100;
}

.sidebar-header {
  padding: 1.5rem;
  display: flex;
  align-items: center;
  gap: 1rem;
  border-bottom: 1px solid var(--border);
}

.logo-box {
  width: 40px;
  height: 40px;
  background: var(--primary);
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.brand-info { display: flex; flex-direction: column; }
.brand-name { font-weight: 800; font-size: 1rem; color: var(--primary); }
.brand-user { font-size: 0.75rem; color: var(--text-muted); font-weight: 500; }

.sidebar-nav { padding: 1.5rem 1rem; flex: 1; }
.nav-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  text-decoration: none;
  color: var(--text-muted);
  font-weight: 600;
  font-size: 0.9rem;
  border-radius: 10px;
  transition: all 0.2s;
  margin-bottom: 0.25rem;
}

.nav-item:hover, .nav-item.router-link-active {
  background: #f1f5f9;
  color: var(--primary);
}

.nav-item svg { transition: color 0.2s; }
.nav-item.router-link-active svg { color: var(--accent); }

.badge-dot {
  width: 8px; height: 8px; background: #ef4444; border-radius: 50%;
  margin-left: auto;
}

.sidebar-footer { padding: 1.5rem; border-top: 1px solid var(--border); }
.logout-btn {
  width: 100%;
  padding: 0.75rem;
  border: 1px solid var(--border);
  background: white;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: all 0.2s;
}
.logout-btn:hover { background: #fef2f2; color: #dc2626; border-color: #fee2e2; }

/* MAIN CONTENT */
.main-content {
  flex: 1;
  margin-left: var(--sidebar-width);
  min-height: 100vh;
}

.main-content.full-width { margin-left: 0; }

/* SHARED UI COMPONENTS (HostoGo Style) */
.glass-card {
  background: white;
  border: 1px solid var(--border);
  border-radius: 20px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
}

.section-title { font-size: 1.5rem; font-weight: 800; color: var(--primary); margin-bottom: 0.5rem; }

@media (max-width: 768px) {
  .sidebar { width: 80px; }
  .brand-info, .brand-name, .brand-user, .nav-item span { display: none; }
  .main-content { margin-left: 80px; }
}
</style>
